// LSP6 Permission constants
// Reference: https://docs.lukso.tech/standards/access-control/lsp6-key-manager#permissions
import type { Address, Hex } from 'viem'

/**
 * ERC725Y key for `LSP4Metadata` (LSP4 Digital Asset metadata).
 * On LSP7/LSP8, `getData` / `getDataForTokenId` with this key expects **LSP2 VerifiableURI** bytes,
 * not raw UTF-8 string bytes — see `encodeLsp4MetadataVerifiableUriValue` in `utils.ts`.
 * @see https://docs.lukso.tech/standards/tokens/LSP4-Digital-Asset-Metadata
 */
export const LSP4_METADATA_DATA_KEY =
  '0x9afb95c6db6149672b83a9cb59ae9f0f2de993440a71e0f87c0aad7b43a0d024' as const satisfies Hex

export const PERMISSIONS = {
  CHANGEOWNER: '0x0000000000000000000000000000000000000000000000000000000000000001',
  ADDCONTROLLER: '0x0000000000000000000000000000000000000000000000000000000000000002',
  EDITPERMISSIONS: '0x0000000000000000000000000000000000000000000000000000000000000004',
  ADDEXTENSIONS: '0x0000000000000000000000000000000000000000000000000000000000000008',
  CHANGEEXTENSIONS: '0x0000000000000000000000000000000000000000000000000000000000000010',
  ADDUNIVERSALRECEIVERDELEGATE: '0x0000000000000000000000000000000000000000000000000000000000000020',
  CHANGEUNIVERSALRECEIVERDELEGATE: '0x0000000000000000000000000000000000000000000000000000000000000040',
  REENTRANCY: '0x0000000000000000000000000000000000000000000000000000000000000080',
  SUPER_TRANSFERVALUE: '0x0000000000000000000000000000000000000000000000000000000000000100',
  TRANSFERVALUE: '0x0000000000000000000000000000000000000000000000000000000000000200',
  SUPER_CALL: '0x0000000000000000000000000000000000000000000000000000000000000400',
  CALL: '0x0000000000000000000000000000000000000000000000000000000000000800',
  SUPER_STATICCALL: '0x0000000000000000000000000000000000000000000000000000000000001000',
  STATICCALL: '0x0000000000000000000000000000000000000000000000000000000000002000',
  SUPER_DELEGATECALL: '0x0000000000000000000000000000000000000000000000000000000000004000',
  DELEGATECALL: '0x0000000000000000000000000000000000000000000000000000000000008000',
  DEPLOY: '0x0000000000000000000000000000000000000000000000000000000000010000',
  SUPER_SETDATA: '0x0000000000000000000000000000000000000000000000000000000000020000',
  SETDATA: '0x0000000000000000000000000000000000000000000000000000000000040000',
  ENCRYPT: '0x0000000000000000000000000000000000000000000000000000000000080000',
  DECRYPT: '0x0000000000000000000000000000000000000000000000000000000000100000',
  SIGN: '0x0000000000000000000000000000000000000000000000000000000000200000',
  EXECUTE_RELAY_CALL: '0x0000000000000000000000000000000000000000000000000000000000400000',
  ALL_PERMISSIONS: '0x00000000000000000000000000000000000000000000000000000000007f3f7f',
} as const

export type PermissionTier = 'safe' | 'change' | 'power'
export type PermissionUISection = 'view' | 'post' | 'spend' | 'admin'

// User-facing grouping for permission selection and warnings.
export const PERMISSION_UI_SECTION: Record<string, PermissionUISection> = {
  // View & verify
  STATICCALL: 'view',
  SUPER_STATICCALL: 'view',
  ENCRYPT: 'view',
  DECRYPT: 'view',
  SIGN: 'view',
  EXECUTE_RELAY_CALL: 'view',

  // Post & interact
  CALL: 'post',
  SETDATA: 'post',
  DEPLOY: 'post',

  // Spend
  TRANSFERVALUE: 'spend',
  SUPER_TRANSFERVALUE: 'spend',

  // Admin & takeover
  ADDCONTROLLER: 'admin',
  EDITPERMISSIONS: 'admin',
  SUPER_CALL: 'admin',
  SUPER_SETDATA: 'admin',
  CHANGEOWNER: 'admin',
  DELEGATECALL: 'admin',
  SUPER_DELEGATECALL: 'admin',
  ADDEXTENSIONS: 'admin',
  CHANGEEXTENSIONS: 'admin',
  ADDUNIVERSALRECEIVERDELEGATE: 'admin',
  CHANGEUNIVERSALRECEIVERDELEGATE: 'admin',
  REENTRANCY: 'admin',
}

// Controller section: capability groups and tag styling (Reading & signing, Profile data, etc.)
export type CapabilityGroupKey =
  | 'identitySigning'
  | 'profileData'
  | 'contractInteractions'
  | 'valueTransfer'
  | 'administrationOwnership'

export const CAPABILITY_GROUPS: Array<{ key: CapabilityGroupKey; label: string; shortLabel: string }> = [
  { key: 'identitySigning', label: 'Reading & signing', shortLabel: 'Reading & signing' },
  { key: 'profileData', label: 'Profile data', shortLabel: 'Profile data' },
  { key: 'contractInteractions', label: 'Contract interactions', shortLabel: 'Contracts' },
  { key: 'valueTransfer', label: 'Value transfer', shortLabel: 'Spend' },
  { key: 'administrationOwnership', label: 'Admin & takeover', shortLabel: 'Admin & takeover' },
]

export const PERMISSION_CAPABILITY_GROUP: Record<string, CapabilityGroupKey> = {
  STATICCALL: 'identitySigning',
  SUPER_STATICCALL: 'identitySigning',
  SIGN: 'identitySigning',
  ENCRYPT: 'identitySigning',
  DECRYPT: 'identitySigning',
  EXECUTE_RELAY_CALL: 'identitySigning',
  SETDATA: 'profileData',
  SUPER_SETDATA: 'profileData',
  CALL: 'contractInteractions',
  SUPER_CALL: 'contractInteractions',
  DEPLOY: 'contractInteractions',
  DELEGATECALL: 'administrationOwnership',
  SUPER_DELEGATECALL: 'administrationOwnership',
  TRANSFERVALUE: 'valueTransfer',
  SUPER_TRANSFERVALUE: 'valueTransfer',
  ADDCONTROLLER: 'administrationOwnership',
  EDITPERMISSIONS: 'administrationOwnership',
  CHANGEOWNER: 'administrationOwnership',
  ADDEXTENSIONS: 'administrationOwnership',
  CHANGEEXTENSIONS: 'administrationOwnership',
  ADDUNIVERSALRECEIVERDELEGATE: 'administrationOwnership',
  CHANGEUNIVERSALRECEIVERDELEGATE: 'administrationOwnership',
  REENTRANCY: 'administrationOwnership',
}

export function groupTagClasses(group: CapabilityGroupKey): string {
  if (group === 'administrationOwnership') {
    return 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-200 border-red-200 dark:border-red-800'
  }
  if (group === 'valueTransfer') {
    return 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-200 border-orange-200 dark:border-orange-800'
  }
  if (group === 'contractInteractions') {
    return 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 border-yellow-200 dark:border-yellow-800'
  }
  if (group === 'profileData') {
    return 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-200 border-blue-200 dark:border-blue-800'
  }
  return 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-200 border-green-200 dark:border-green-800'
}

/** Pill style for controller-section tags (rounded-[4px]) */
export function permissionPillClasses(classes: string): string {
  return `inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-[4px] border ${classes}`
}

/** Detail pill: two-line tag (label + technical) */
export function permissionDetailPillClasses(classes: string): string {
  return `inline-flex flex-col items-start gap-0.5 px-2.5 py-1.5 rounded-lg border ${classes}`
}

// Developer-facing / technical permission names (small grey text in UI)
export const PERMISSION_TECHNICAL_NAMES: Record<string, string> = {
  CHANGEOWNER: 'Change Owner',
  ADDCONTROLLER: 'Add Controller',
  EDITPERMISSIONS: 'Edit Permissions',
  ADDEXTENSIONS: 'Add Extensions',
  CHANGEEXTENSIONS: 'Change Extensions',
  ADDUNIVERSALRECEIVERDELEGATE: 'Add Universal Receiver Delegate',
  CHANGEUNIVERSALRECEIVERDELEGATE: 'Change Universal Receiver Delegate',
  REENTRANCY: 'Reentrancy',
  SUPER_TRANSFERVALUE: 'Super Transfer Value',
  TRANSFERVALUE: 'Transfer Value',
  SUPER_CALL: 'Super Call',
  CALL: 'Call',
  SUPER_STATICCALL: 'Super Static Call',
  STATICCALL: 'Static Call',
  SUPER_DELEGATECALL: 'Super Delegate Call',
  DELEGATECALL: 'Delegate Call',
  DEPLOY: 'Deploy',
  SUPER_SETDATA: 'Super Set Data',
  SETDATA: 'Set Data',
  ENCRYPT: 'Encrypt',
  DECRYPT: 'Decrypt',
  SIGN: 'Sign',
  EXECUTE_RELAY_CALL: 'Execute Relay Call',
}

// Permission names for everyday users (primary UI label)
export const PERMISSION_NAMES: Record<string, string> = {
  // 🟢 Safe (read-only)
  STATICCALL: 'Read From Contracts',
  SUPER_STATICCALL: 'Read From All Contracts',
  SIGN: 'Sign Messages as Me',
  EXECUTE_RELAY_CALL: 'Send Gasless Transactions',
  ENCRYPT: 'Encrypt Data',
  DECRYPT: 'Decrypt Data',

  // 🟡 Change & value
  TRANSFERVALUE: 'Spend My LYX',
  CALL: 'Interact With Contracts',
  SETDATA: 'Edit My Profile',
  DEPLOY: 'Create Contracts in My Name',

  // 🔴 High risk / powerful control
  ADDCONTROLLER: 'Add New Controllers',
  EDITPERMISSIONS: 'Change My Permissions',
  SUPER_CALL: 'Interact With All Contracts',
  DELEGATECALL: 'Execute Code as My Profile',
  SUPER_DELEGATECALL: 'Execute Any Code as My Profile',
  SUPER_SETDATA: 'Edit Anything on My Profile',
  SUPER_TRANSFERVALUE: 'Spend Any Amount of LYX',
  CHANGEOWNER: 'Transfer Profile Ownership',
  ADDEXTENSIONS: 'Add Extensions to My Profile',
  CHANGEEXTENSIONS: 'Change My Profile Extensions',
  ADDUNIVERSALRECEIVERDELEGATE: 'Add a Receiver Delegate',
  CHANGEUNIVERSALRECEIVERDELEGATE: 'Change Receiver Delegate',
  REENTRANCY: 'Allow Re‑entrancy',
}

// Simple, user-facing risk tiering (cards + badges)
export const PERMISSION_TIER: Record<string, PermissionTier> = {
  // 🟢 Safe (read-only)
  STATICCALL: 'safe',
  SUPER_STATICCALL: 'safe',
  SIGN: 'safe',
  EXECUTE_RELAY_CALL: 'safe',
  ENCRYPT: 'safe',
  DECRYPT: 'safe',

  // 🟡 Change & value
  TRANSFERVALUE: 'change',
  CALL: 'change',
  SETDATA: 'change',
  DEPLOY: 'change',

  // 🔴 Powerful control
  ADDCONTROLLER: 'power',
  EDITPERMISSIONS: 'power',
  SUPER_CALL: 'power',
  DELEGATECALL: 'power',
  SUPER_DELEGATECALL: 'power',
  SUPER_SETDATA: 'power',
  SUPER_TRANSFERVALUE: 'power',
  CHANGEOWNER: 'power',
  ADDEXTENSIONS: 'power',
  CHANGEEXTENSIONS: 'power',
  ADDUNIVERSALRECEIVERDELEGATE: 'power',
  CHANGEUNIVERSALRECEIVERDELEGATE: 'power',
  REENTRANCY: 'power',
}

// Permission risk levels (legacy; still used for validation + some lists)
// NOTE: These are more granular than tiers. UI should prefer PERMISSION_TIER.
export const PERMISSION_RISK: Record<string, 'safe' | 'medium' | 'high' | 'critical'> = {
  CHANGEOWNER: 'critical',
  SUPER_DELEGATECALL: 'critical',
  DELEGATECALL: 'critical',

  ADDCONTROLLER: 'high',
  EDITPERMISSIONS: 'high',
  SUPER_CALL: 'high',
  SUPER_SETDATA: 'high',
  SUPER_TRANSFERVALUE: 'high',
  REENTRANCY: 'high',
  ADDEXTENSIONS: 'high',
  CHANGEEXTENSIONS: 'high',
  ADDUNIVERSALRECEIVERDELEGATE: 'high',
  CHANGEUNIVERSALRECEIVERDELEGATE: 'high',

  TRANSFERVALUE: 'medium',
  CALL: 'medium',
  SETDATA: 'medium',
  DEPLOY: 'medium',

  SUPER_STATICCALL: 'safe',
  STATICCALL: 'safe',
  ENCRYPT: 'safe',
  DECRYPT: 'safe',
  SIGN: 'safe',
  EXECUTE_RELAY_CALL: 'safe',
}

export const PERMISSION_ICONS: Record<string, 'book' | 'signature' | 'rocket' | 'coins' | 'arrows' | 'pencil' | 'crane' | 'users' | 'sliders' | 'spark' | 'crown' | 'shield'> = {
  STATICCALL: 'book',
  SUPER_STATICCALL: 'book',
  SIGN: 'signature',
  EXECUTE_RELAY_CALL: 'rocket',
  TRANSFERVALUE: 'coins',
  SUPER_TRANSFERVALUE: 'coins',
  CALL: 'arrows',
  SUPER_CALL: 'shield',
  SETDATA: 'pencil',
  SUPER_SETDATA: 'pencil',
  DEPLOY: 'crane',
  ADDCONTROLLER: 'users',
  EDITPERMISSIONS: 'sliders',
  DELEGATECALL: 'spark',
  SUPER_DELEGATECALL: 'spark',
  CHANGEOWNER: 'crown',
}

// Permission descriptions (short subtitle: "Enables controller to ...") — aligned with LSP6 Key Manager
export const PERMISSION_DESCRIPTIONS: Record<string, string> = {
  STATICCALL: 'Enables controller to make read-only calls to specific contracts.',
  SUPER_STATICCALL: 'Same as STATICCALL but allows calling any contract; still read-only.',
  SIGN: 'Enables controller to sign messages on behalf of your profile.',
  EXECUTE_RELAY_CALL: 'Lets the controller act without paying gas.',
  ENCRYPT: 'Enables controller to encrypt data on behalf of your profile.',
  DECRYPT: 'Enables controller to decrypt data on behalf of your profile.',
  TRANSFERVALUE: 'Enables controller to transfer LYX from your profile’s balance, with restrictions.',
  SUPER_TRANSFERVALUE: 'Same as TRANSFERVALUE but to any address; Allowed Calls restrictions are not checked.',
  CALL: 'Enables controller to use your profile to interact with specific contracts; read and write.',
  SUPER_CALL: 'Same as CALL but allows interacting with any contract; Allowed Calls restrictions are not checked.',
  SETDATA: 'Enables controller to set data in your profile’s storage, restricted to specific data keys (Allowed ERC725Y Data Keys).',
  SUPER_SETDATA: 'Same as SETDATA but allows setting any data key; Allowed ERC725Y Data Keys restrictions are not checked.',
  DEPLOY: 'Enables controller to deploy new contracts through your profile.',
  ADDCONTROLLER: 'Enables controller to grant permissions to new addresses (add new controllers).',
  EDITPERMISSIONS: 'Enables controller to change existing permissions of controllers (or remove them); can also edit its own permissions.',
  DELEGATECALL: 'Enables controller to run code from other contracts in your profile’s context.',
  SUPER_DELEGATECALL: 'Same as DELEGATECALL but for any contract; Allowed Calls are not checked.',
  CHANGEOWNER: 'Enables controller to change the owner of the linked profile.',
  ADDEXTENSIONS: 'Enables controller to add new LSP17 extension contracts on your profile.',
  CHANGEEXTENSIONS: 'Enables controller to edit the address of extension contracts already set on your profile.',
  ADDUNIVERSALRECEIVERDELEGATE: 'Enables controller to add LSP1 Universal Receiver Delegate addresses (react to incoming assets/messages).',
  CHANGEUNIVERSALRECEIVERDELEGATE: 'Enables controller to edit LSP1 Universal Receiver Delegate addresses.',
  REENTRANCY: 'Enables controller to execute a payload during another payload’s execution (re-enter); advanced, use with care.',
}

// Layman detail for the expandable "Details" section — aligned with LSP6 Key Manager
export const PERMISSION_DETAILS: Record<string, { detail: string }> = {
  STATICCALL: {
    detail: 'The controller can use your profile to make external calls to other contracts using the STATICCALL opcode. Those calls are read-only: they cannot change state at the target contract. You can restrict which contracts or functions can be called via Allowed Calls.',
  },
  SUPER_STATICCALL: {
    detail: 'Same as STATICCALL but the controller can call any contract—Allowed Calls restrictions are not checked. Calls are still read-only.',
  },
  SIGN: {
    detail: 'The controller can authenticate on behalf of your Universal Profile. It is used primarily to sign login messages (e.g. in Web 2.0–style apps) or other messages that prove ownership of the profile.',
  },
  EXECUTE_RELAY_CALL: {
    detail: 'The controller can sign a payload (e.g. a transaction) that someone else (a relayer) can then submit to the Key Manager. The permission is checked against the controller that signed the relay call. This lets the controller approve actions without paying gas—the relayer pays instead.',
  },
  ENCRYPT: {
    detail: 'The controller can encrypt data or messages on behalf of your profile. Developers use this for things like private messaging or secure storage that only your profile (or chosen recipients) can decrypt.',
  },
  DECRYPT: {
    detail: 'The controller can decrypt data or messages that were encrypted for your profile. This is needed to read private content or recover access when encryption was used.',
  },
  TRANSFERVALUE: {
    detail: 'The controller can transfer native LYX from your profile’s balance to other addresses, with restrictions. You limit where it can send via Allowed Calls (e.g. specific addresses or contracts implementing certain interfaces). For a simple LYX transfer, no extra data is sent; to send data with LYX, the controller also needs the CALL permission.',
  },
  SUPER_TRANSFERVALUE: {
    detail: 'Same as TRANSFERVALUE but the controller can send LYX to any address. Allowed Calls restrictions are not checked. Use when you trust the controller with unrestricted value transfer.',
  },
  CALL: {
    detail: 'The controller can use your profile to call functions on other contracts. Unlike STATICCALL, these calls use the CALL opcode and can change state at the target (e.g. swap tokens, stake, update contract storage). You can restrict which addresses, interfaces, or functions are allowed via Allowed Calls. If no Allowed Calls are set, the controller cannot interact with any address.',
  },
  SUPER_CALL: {
    detail: 'Same as CALL but the controller can interact with any contract. Allowed Calls restrictions are not checked. Full power to call any function on any contract; grant only when necessary.',
  },
  SETDATA: {
    detail: 'The controller can write data into your profile’s ERC725Y key-value store, but only for data keys you list in Allowed ERC725Y Data Keys. It cannot set permission-related keys (needs ADDCONTROLLER or EDITPERMISSIONS), LSP1 receiver keys (needs ADD/CHANGE Universal Receiver Delegate), or LSP17 extension keys (needs ADDEXTENSIONS or CHANGEEXTENSIONS). If no allowed data keys are set, the controller cannot set any value.',
  },
  SUPER_SETDATA: {
    detail: 'Same as SETDATA but the controller can set any ERC725Y data key. Allowed ERC725Y Data Keys restrictions are not checked. If the controller has restrictions configured, they are ignored—use with caution.',
  },
  DEPLOY: {
    detail: 'The controller can use your profile to deploy new smart contracts on the network. Your profile is recorded as the deployer (CREATE or CREATE2). Useful for deploying vaults, extensions, or other contracts linked to your identity.',
  },
  ADDCONTROLLER: {
    detail: 'The controller can grant permissions to new addresses that do not yet have any. That means adding a new entry in the list of controllers, setting its permissions, and increasing the controller list length. Only grant this to addresses you trust to add further controllers or permissions.',
  },
  EDITPERMISSIONS: {
    detail: 'The controller can change the permissions of any controller that already has permissions set (including itself), and can remove controllers or edit their addresses in the list. Be aware: a controller with EDITPERMISSIONS can also edit its own permissions.',
  },
  DELEGATECALL: {
    detail: 'The controller can run code from another contract in your profile’s context (the code executes as if it were part of your account). This is very powerful and risky—malicious code could take over the account. Only grant to contracts you fully trust.',
  },
  SUPER_DELEGATECALL: {
    detail: 'Same as DELEGATECALL but the controller can run code from any contract—Allowed Calls restrictions are not checked. Maximum power; only grant to code you fully trust.',
  },
  CHANGEOWNER: {
    detail: 'The controller can change the owner of the linked ERC725Account. This is how you upgrade the Key Manager: transfer ownership to a new Key Manager contract. If used, the current owner (e.g. Key Manager) loses control; use only when you intend to upgrade or transfer ownership.',
  },
  ADDEXTENSIONS: {
    detail: 'The controller can add new LSP17 extension contracts to your profile for specific function selectors. When your profile is called with a function it does not implement, the fallback can forward the call to the right extension.',
  },
  CHANGEEXTENSIONS: {
    detail: 'The controller can edit the address of LSP17 extension contracts already set on your profile (for given function selectors). The fallback will then use the new extension when those functions are called.',
  },
  ADDUNIVERSALRECEIVERDELEGATE: {
    detail: 'The controller can add LSP1 Universal Receiver Delegate addresses for specific Type IDs. These delegates are notified when certain assets or messages are sent to your profile, so you can implement custom handling (e.g. for tokens). If no delegate was set yet, this also allows setting the default LSP1 delegate.',
  },
  CHANGEUNIVERSALRECEIVERDELEGATE: {
    detail: 'The controller can edit the default LSP1 Universal Receiver Delegate address or the delegate addresses linked to specific Type IDs. Use this to update how your profile reacts to incoming assets or messages.',
  },
  REENTRANCY: {
    detail: 'The controller can execute a payload while another payload is already being executed (re-entrancy). For example: your profile calls contract A, A does some work, then A calls back your profile with another payload. Contracts do this via execute(...), EOAs via executeRelayCall(...). Useful for composable flows; only enable if you understand and trust the contracts involved.',
  },
}

// Data keys for permissions
export const DATA_KEYS = {
  'AddressPermissions[]': '0xdf30dba06db6a30e65354d9a64c609861f089545ca58c6b4dbe31a5f338cb0e3',
  'AddressPermissions[]_length': '0xdf30dba06db6a30e65354d9a64c609861f089545ca58c6b4dbe31a5f338cb0e3',
  'AddressPermissions[]_index_prefix': '0xdf30dba06db6a30e65354d9a64c60986',
  'AddressPermissions:Permissions_prefix': '0x4b80742de2bf82acb3630000',
  'AddressPermissions:AllowedCalls_prefix': '0x4b80742de2bf393a64c70000',
  'AddressPermissions:AllowedERC725YDataKeys_prefix': '0x4b80742de2bf866c29110000',
  // LSP3 Profile
  'LSP3Profile': '0x5ef83ad9559033e6e941db7d7c495acdce616347d28e90c7ce47cbfcfcad3bc5',
}

// LSP0 (Universal Profile) ABI - minimal for our needs
export const LSP0_ABI = [
  {
    inputs: [{ name: 'dataKey', type: 'bytes32' }],
    name: 'getData',
    outputs: [{ name: 'dataValue', type: 'bytes' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ name: 'dataKeys', type: 'bytes32[]' }],
    name: 'getDataBatch',
    outputs: [{ name: 'dataValues', type: 'bytes[]' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { name: 'dataKeys', type: 'bytes32[]' },
      { name: 'dataValues', type: 'bytes[]' },
    ],
    name: 'setDataBatch',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [],
    name: 'owner',
    outputs: [{ name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { name: 'operationType', type: 'uint256' },
      { name: 'target', type: 'address' },
      { name: 'value', type: 'uint256' },
      { name: 'data', type: 'bytes' },
    ],
    name: 'execute',
    outputs: [{ name: 'result', type: 'bytes' }],
    stateMutability: 'payable',
    type: 'function',
  },
] as const

// LSP6 Key Manager ABI - minimal for relay execute
export const LSP6_KEY_MANAGER_ABI = [
  {
    inputs: [{ name: 'payload', type: 'bytes' }],
    name: 'execute',
    outputs: [{ name: 'result', type: 'bytes' }],
    stateMutability: 'payable',
    type: 'function',
  },
] as const

// RPC URLs: set VITE_LUKSO_MAINNET_RPC_URL / VITE_LUKSO_TESTNET_RPC_URL in .env to use a pro RPC and avoid timeouts
// Defaults match walletConfig.ts (public LUKSO RPCs — not thirdweb).
const LUKSO_MAINNET_RPC = import.meta.env.VITE_LUKSO_MAINNET_RPC_URL || 'https://rpc.mainnet.lukso.network'
const LUKSO_TESTNET_RPC = import.meta.env.VITE_LUKSO_TESTNET_RPC_URL || 'https://rpc.testnet.lukso.network'

// Chain configurations
export function rpcHttpUrlForChainId(chainId: number): string {
  if (chainId === 4201) return CHAINS.luksoTestnet.rpcUrls.default.http[0]
  return CHAINS.lukso.rpcUrls.default.http[0]
}

export const CHAINS = {
  lukso: {
    id: 42,
    name: 'LUKSO',
    network: 'lukso',
    nativeCurrency: { name: 'LYX', symbol: 'LYX', decimals: 18 },
    rpcUrls: {
      default: { http: [LUKSO_MAINNET_RPC] },
      public: { http: [LUKSO_MAINNET_RPC] },
    },
    blockExplorers: {
      default: { name: 'LUKSO Explorer', url: 'https://explorer.execution.mainnet.lukso.network' },
    },
  },
  luksoTestnet: {
    id: 4201,
    name: 'LUKSO Testnet',
    network: 'lukso-testnet',
    nativeCurrency: { name: 'LYXt', symbol: 'LYXt', decimals: 18 },
    rpcUrls: {
      default: { http: [LUKSO_TESTNET_RPC] },
      public: { http: [LUKSO_TESTNET_RPC] },
    },
    blockExplorers: {
      default: { name: 'LUKSO Testnet Explorer', url: 'https://explorer.execution.testnet.lukso.network' },
    },
  },
} as const

// AllowedCalls tuple call-type bitmap (LSP6 — must match https://docs.lukso.tech/standards/access-control/lsp6-key-manager#allowed-calls)
export const CALL_TYPES = {
  TRANSFERVALUE: 1,
  CALL: 2,
  STATICCALL: 4,
  DELEGATECALL: 8,
} as const

// Interface ID presets for AllowedCalls
export const INTERFACE_PRESETS: Record<string, { name: string; id: string }> = {
  LSP7: { name: 'LSP7 (Token)', id: '0xc52d6008' },
  LSP8: { name: 'LSP8 (NFT)', id: '0x3a271706' },
  ERC20: { name: 'ERC20', id: '0x36372b07' },
  ERC721: { name: 'ERC721', id: '0x80ac58cd' },
  ERC1155: { name: 'ERC1155', id: '0xd9b67a26' },
}

// ERC725Y Data Key presets for AllowedERC725YDataKeys
export interface DataKeyPreset {
  name: string
  key: string
  description: string
  group: string
  keyType: 'Singleton' | 'Mapping' | 'Array'
}

export const DATA_KEY_PRESETS: Record<string, DataKeyPreset> = {
  LSP3Profile: {
    name: 'LSP3Profile',
    key: '0x5ef83ad9559033e6e941db7d7c495acdce616347d28e90c7ce47cbfcfcad3bc5',
    description: 'Profile metadata (name, description, avatar, etc.)',
    group: 'Profile',
    keyType: 'Singleton',
  },
  'LSP5ReceivedAssets[]': {
    name: 'LSP5ReceivedAssets[]',
    key: '0x6460ee3c0aac563ccbf76d6e1d07bada78e3a9514e6382b736ed3f478ab7b90b',
    description: 'Received assets array length',
    group: 'Assets',
    keyType: 'Array',
  },
  LSP5ReceivedAssetsMap: {
    name: 'LSP5ReceivedAssetsMap',
    key: '0x812c4334633eb816c80d0000',
    description: 'Received asset mapping entries',
    group: 'Assets',
    keyType: 'Mapping',
  },
  'LSP12IssuedAssets[]': {
    name: 'LSP12IssuedAssets[]',
    key: '0x7c8c3416d6cda87cd42c71ea1843df28ac4850354f988d55ee2eaa47b6dc05cd',
    description: 'Issued assets array length',
    group: 'Assets',
    keyType: 'Array',
  },
  LSP12IssuedAssetsMap: {
    name: 'LSP12IssuedAssetsMap',
    key: '0x74ac2555c10b9349e78f0000',
    description: 'Issued asset mapping entries',
    group: 'Assets',
    keyType: 'Mapping',
  },
  LSP1UniversalReceiverDelegate: {
    name: 'LSP1 Universal Receiver Delegate',
    key: '0x0cfc51aec37c55a4d0b1a65c6255c4bf2fbdf6277f3cc0730c45b828b6db8b47',
    description: 'Universal Receiver Delegate address',
    group: 'Admin & takeover',
    keyType: 'Singleton',
  },
  LSP17Extension: {
    name: 'LSP17 Extensions',
    key: '0xcee78b4094da860110960000',
    description: 'Contract extension mappings',
    group: 'Admin & takeover',
    keyType: 'Mapping',
  },
}

// Permission presets
export interface PermissionPreset {
  name: string
  description: string
  permissions: bigint
  recommended?: boolean
  warning?: string
  allowedCalls?: string
  allowedDataKeys?: string
}

// Note: ALL presets include SIGN permission for quota checking support
export const PERMISSION_PRESETS: Record<string, PermissionPreset> = {
  'token-operator': {
    name: 'Token Operator',
    description: 'Can transfer tokens and NFTs, call any contract, and sign messages.',
    permissions: BigInt(PERMISSIONS.SUPER_CALL) | BigInt(PERMISSIONS.TRANSFERVALUE) | BigInt(PERMISSIONS.EXECUTE_RELAY_CALL) | BigInt(PERMISSIONS.SIGN),
    recommended: false,
  },
  'profile-manager': {
    name: 'Profile Manager',
    description: 'Can update profile metadata, read data, and sign messages.',
    permissions: BigInt(PERMISSIONS.SUPER_SETDATA) | BigInt(PERMISSIONS.STATICCALL) | BigInt(PERMISSIONS.EXECUTE_RELAY_CALL) | BigInt(PERMISSIONS.SIGN),
    recommended: false,
  },
  'wallet': {
    name: 'Wallet',
    description: 'Full wallet capabilities: transfer tokens, update profile, interact with contracts, and sign messages.',
    permissions: BigInt(PERMISSIONS.SUPER_CALL) | BigInt(PERMISSIONS.TRANSFERVALUE) | BigInt(PERMISSIONS.SUPER_SETDATA) | BigInt(PERMISSIONS.STATICCALL) | BigInt(PERMISSIONS.SIGN) | BigInt(PERMISSIONS.EXECUTE_RELAY_CALL),
    recommended: true,
  },
  'full-access': {
    name: 'Full Access',
    description: 'Complete control over the profile. Use with extreme caution!',
    permissions: BigInt(PERMISSIONS.ALL_PERMISSIONS),
    recommended: false,
    warning: 'This grants FULL CONTROL over your Universal Profile. The controller can transfer all assets, change ownership, and perform any action. Only grant this to controllers you absolutely trust.',
  },
}

/** LSP8 PermissionControl collection (templates + mint fee). */
export const PERMISSION_CONTROL = {
  chainId: 42,
  address: (import.meta.env.VITE_PERMISSIONCONTROL_CONTRACT as Address | undefined) ??
    ('0x65Cb19C9c7b5A6695C48936Cf8917CF34438D7D3' as Address),
  /** Log scan start block; override with `VITE_PERMISSIONCONTROL_DEPLOYMENT_BLOCK` if needed. */
  deploymentBlock: BigInt(import.meta.env.VITE_PERMISSIONCONTROL_DEPLOYMENT_BLOCK ?? '7177778'),
} as const

/** @deprecated Use PERMISSION_CONTROL — kept for gradual migration */
export const PERMISSIONS_ONCHAIN_TEMPLATE_REGISTRY = PERMISSION_CONTROL

/** Relative path to the in-app agent skill page (respects `import.meta.env.BASE_URL`). */
export function getSkillPagePath(): string {
  const base = import.meta.env.BASE_URL.endsWith('/') ? import.meta.env.BASE_URL : `${import.meta.env.BASE_URL}/`
  return `${base}skill`.replace(/\/{2,}/g, '/')
}

/** Absolute URL for the agent skill page (for copy-to-clipboard). Override with `VITE_AGENT_TEMPLATE_DOCS_URL`. */
export function getSkillPageAbsoluteUrl(): string {
  const override = import.meta.env.VITE_AGENT_TEMPLATE_DOCS_URL?.trim()
  if (override) return override
  if (typeof window === 'undefined') return getSkillPagePath()
  const path = getSkillPagePath()
  return `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`.replace(/([^:]\/)\/+/g, '$1')
}

/** Static Markdown at `/skill.md` (plain HTTP body; no JS required). */
export function getSkillMarkdownPath(): string {
  const base = import.meta.env.BASE_URL.endsWith('/') ? import.meta.env.BASE_URL : `${import.meta.env.BASE_URL}/`
  return `${base}skill.md`.replace(/\/{2,}/g, '/')
}

/** Absolute URL to raw `skill.md`. Override with `VITE_AGENT_SKILL_MARKDOWN_URL` for forks/CDNs. */
export function getSkillMarkdownAbsoluteUrl(): string {
  const override = import.meta.env.VITE_AGENT_SKILL_MARKDOWN_URL?.trim()
  if (override) return override
  if (typeof window === 'undefined') return getSkillMarkdownPath()
  const path = getSkillMarkdownPath()
  return `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`.replace(/([^:]\/)\/+/g, '$1')
}

function staticAssetPath(filename: string): string {
  const base = import.meta.env.BASE_URL.endsWith('/') ? import.meta.env.BASE_URL : `${import.meta.env.BASE_URL}/`
  return `${base}${filename}`.replace(/\/{2,}/g, '/')
}

/** Static JSON ABI at `/permission-control-abi.json` (generated from `permissionControl.ts` at build). */
export function getPermissionControlAbiJsonPath(): string {
  return staticAssetPath('permission-control-abi.json')
}

/** Absolute URL to PermissionControl ABI JSON for agents. Override with `VITE_AGENT_PERMISSION_CONTROL_ABI_URL`. */
export function getPermissionControlAbiJsonAbsoluteUrl(): string {
  const override = import.meta.env.VITE_AGENT_PERMISSION_CONTROL_ABI_URL?.trim()
  if (override) return override
  if (typeof window === 'undefined') return getPermissionControlAbiJsonPath()
  const path = getPermissionControlAbiJsonPath()
  return `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`.replace(/([^:]\/)\/+/g, '$1')
}
